import { Geolocation, Position } from "@capacitor/geolocation";
import { Capacitor } from "@capacitor/core";
import { AppLauncher } from "@capacitor/app-launcher";
import { LocationLog } from "./models/LocationLog";

const LOCATION_CACHE_MAX_AGE_MILLISECONDS = 5 * 60 * 1000;

export enum LocationErrorCode {
    PERMISSION_DENIED = "PERMISSION_DENIED",
    PERMISSION_PROMPT_DISMISSED = "PERMISSION_PROMPT_DISMISSED",
    LOCATION_SERVICES_DISABLED = "LOCATION_SERVICES_DISABLED",
    TIMEOUT = "TIMEOUT",
    UNAVAILABLE = "UNAVAILABLE",
    UNKNOWN = "UNKNOWN",
}

export interface LocationError {
    key: LocationErrorCode;
    message: string;
}

export const LocationErrors = {
    PermissionDenied: {
        key: LocationErrorCode.PERMISSION_DENIED,
        message: "To continue, allow location access in your phone settings. We use it to get your current position.",
    } as LocationError,
    PermissionPromptDismissed: {
        key: LocationErrorCode.PERMISSION_PROMPT_DISMISSED,
        message: "Please allow location access to continue. You can enable it in Settings.",
    } as LocationError,
    LocationServicesDisabled: {
        key: LocationErrorCode.LOCATION_SERVICES_DISABLED,
        message: "Location Services (GPS) are turned off. Turn them on, then try again.",
    } as LocationError,
    Timeout: {
        key: LocationErrorCode.TIMEOUT,
        message:
            "This is taking longer than expected. Check your GPS signal or move closer to a window, and then try again.",
    } as LocationError,
    Unavailable: {
        key: LocationErrorCode.UNAVAILABLE,
        message: "We couldn’t read your location right now. Please try again in a moment.",
    } as LocationError,
    Unknown: {
        key: LocationErrorCode.UNKNOWN,
        message: "We couldn’t access your location. Please try again, or check your device settings.",
    } as LocationError,
} as const;

export const isLocationError = (error: unknown): error is LocationError =>
    typeof error === "object" && error !== null && "key" in error && "message" in error;

export const mapGeolocationError = (e: unknown): LocationError => {
    const err = typeof e === "object" && e !== null ? (e as { code?: unknown; message?: string }) : {};
    const code = err.code;
    const msg = (err.message ?? "").toLowerCase();

    // ---- Capacitor v7 native plugin error codes (string) ----
    if (typeof code === "string") {
        switch (code) {
            // Permission denied
            case "OS-PLUG-GLOC-0003":
                return LocationErrors.PermissionDenied;

            // Location services disabled / enable request denied
            case "OS-PLUG-GLOC-0007":
            case "OS-PLUG-GLOC-0009":
                return LocationErrors.LocationServicesDisabled;

            // Timeout
            case "OS-PLUG-GLOC-0010":
                return LocationErrors.Timeout;

            // Catch-all "couldn't obtain"
            case "OS-PLUG-GLOC-0002":
                return LocationErrors.Unavailable;

            // Optional: treat invalid input as Unknown (or a dedicated error if you have one)
            case "OS-PLUG-GLOC-0004":
            case "OS-PLUG-GLOC-0005":
            case "OS-PLUG-GLOC-0006":
                return LocationErrors.Unknown;

            // Google Play / settings errors often manifest as "unavailable"
            case "OS-PLUG-GLOC-0014":
            case "OS-PLUG-GLOC-0015":
            case "OS-PLUG-GLOC-0016":
                return LocationErrors.Unavailable;

            default:
                break;
        }
    }

    // ---- Web / browser GeolocationPositionError codes (number) ----
    // 1 = PERMISSION_DENIED, 2 = POSITION_UNAVAILABLE, 3 = TIMEOUT
    if (typeof code === "number") {
        if (code === 1) return LocationErrors.PermissionDenied;
        if (code === 2) return LocationErrors.Unavailable;
        if (code === 3) return LocationErrors.Timeout;
    }

    // ---- Heuristic fallback based on message ----
    if (
        msg.includes("location services") ||
        msg.includes("gps") ||
        msg.includes("disabled") ||
        msg.includes("turned off")
    )
        return LocationErrors.LocationServicesDisabled;
    if (msg.includes("timeout") || msg.includes("in time")) return LocationErrors.Timeout;
    if (msg.includes("denied") || msg.includes("permission")) return LocationErrors.PermissionDenied;

    return LocationErrors.Unknown;
};

export type GetPositionOptions = {
    enableHighAccuracy?: boolean;
    timeout?: number;
    maximumAge?: number;
};

const isLocationFresh = (
    lastAccess: LocationLog | undefined,
    maxAgeMilliseconds: number = LOCATION_CACHE_MAX_AGE_MILLISECONDS
): boolean => {
    if (!lastAccess) return false;
    const ageMs = maxAgeMilliseconds;
    return new Date().getTime() - new Date(lastAccess.date).getTime() < ageMs;
};

const getCurrentPosition = async (options: GetPositionOptions = {}): Promise<Position> => {
    return Geolocation.getCurrentPosition({
        enableHighAccuracy: options.enableHighAccuracy ?? true,
        maximumAge: options.maximumAge ?? LOCATION_CACHE_MAX_AGE_MILLISECONDS,
        ...options,
    })
        .then((pos) => pos)
        .catch((e) => {
            throw mapGeolocationError(e);
        });
};

const getLocationLog = async (userId: string, options: GetPositionOptions = {}): Promise<LocationLog> => {
    return getCurrentPosition(options)
        .then((geolocationPosition) => {
            return createLastAccess(userId, geolocationPosition.coords.latitude, geolocationPosition.coords.longitude);
        })
        .catch((e) => {
            throw e;
        });
};

const createLastAccess = (userId: string, latitude: number, longitude: number): LocationLog => {
    const lastAccess: LocationLog = {
        pendingSync: true,
        userId: userId,
        date: new Date(),
        latitude,
        longitude,
    };
    return lastAccess;
};

const requestPermissions = async (): Promise<void> => {
    if (!Capacitor.isNativePlatform()) return;

    const status = await Geolocation.checkPermissions();

    if (status.location === "prompt" || status.location === "prompt-with-rationale") {
        // OS dialog has not been permanently dismissed — show it
        await Geolocation.requestPermissions({ permissions: ["location"] });
        return;
    }

    if (status.location === "denied") {
        // OS will no longer show the dialog — send the user to app settings
        if (Capacitor.getPlatform() === "ios") {
            await AppLauncher.openUrl({ url: "app-settings:" });
        }
        // Android has no reliable URI scheme for app settings without a native plugin;
        // the existing error message already directs the user to phone settings.
    }
};

const locationService = {
    getCurrentPosition,
    isLocationFresh,
    getLocationLog,
    createLastAccess,
    requestPermissions,
};

export default locationService;
